import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { TagInput } from '@nucleus-commerce/admin-kit/forms';
import type { Order } from '@nucleus-commerce/api-client/admin';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import { Label } from '@nucleus-commerce/ui/components/label';
import { toast } from '@nucleus-commerce/ui/components/sonner';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useUpdateOrder } from '../hooks/use-order-mutations';

export interface OrderNotesCardProps {
  storeId: string;
  order: Order;
  readOnly?: boolean;
}

/** Tags save on every change, staff notes when the field loses focus. */
export function OrderNotesCard({ storeId, order, readOnly = false }: OrderNotesCardProps) {
  const { t } = useTranslation('orders');
  const message = useErrorMessage();
  const update = useUpdateOrder(storeId, 'orders:toasts.updated');
  const [notes, setNotes] = useState(order.staffNotes ?? '');
  // Resync when another action (mark paid, cancel) replaced the order in the cache.
  const [seen, setSeen] = useState(order.updatedAt);
  if (seen !== order.updatedAt) {
    setSeen(order.updatedAt);
    setNotes(order.staffNotes ?? '');
  }

  const save = (body: Parameters<typeof update.mutate>[0]['body']) =>
    update.mutate({ id: order.id, body }, { onError: (error) => toast.error(message(error)) });

  return (
    <Card className="gap-3">
      <CardHeader>
        <CardTitle>{t('notes.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="order-tags">{t('notes.tags')}</Label>
          <TagInput
            id="order-tags"
            value={order.tags}
            onChange={(tags) => save({ tags })}
            placeholder={t('notes.tagsPlaceholder')}
            removeLabel={(tag) => t('notes.removeTag', { tag })}
            disabled={readOnly || update.isPending}
            max={50}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="order-staff-notes">{t('notes.staffNotes')}</Label>
          <Textarea
            id="order-staff-notes"
            rows={4}
            value={notes}
            placeholder={t('notes.staffNotesPlaceholder')}
            disabled={readOnly}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => {
              const next = notes.trim() === '' ? null : notes;
              if (next !== order.staffNotes) save({ staffNotes: next });
            }}
          />
          <p className="text-muted-foreground text-xs">{t('notes.staffNotesHint')}</p>
        </div>
      </CardContent>
    </Card>
  );
}
